import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectAllNodes,
  selectAllPoints,
  selectFilteredInterconnects,
  selectFilteredNodes,
  selectFilteredPoints,
  selectFilteredTsps,
} from './slice/mapSelectors';
import {
  selectLabelDisplayOptions,
  selectNodeSize,
} from '../plotter/plotterSelectors';
import { isCompressor } from '../utils/pointUtils';
import { fromLonLat } from 'ol/proj';
import { useTheme } from '../helpers/hooks';
import { FlatStyleLike } from 'ol/style/flat';
import { useMapZoom } from '../hooks/useMapZoom';
import {
  colors,
  getContrastColor,
  setLuminosity,
  setSaturation,
} from '../utils/colors';
import { MAX_TSPS_LABELS_THRESHOLD } from '../plotter/plotterSlice';
import { OlLayerType, useMapLayer } from '../hooks/useMapLayer';
import { FeatureLike } from '../utils/geoJsonUtils';

const COMPRESSOR_LABEL_ZOOM_THRESHOLD = 8.3;
const INTERCONNECT_LABEL_ZOOM_THRESHOLD = 10.2;
const POINT_LABEL_ZOOM_THRESHOLD = 11.5;

export const getLabelStyle = (nodeSize: number): FlatStyleLike => {
  return [
    {
      filter: ['==', ['get', 'type'], 'point'],
      style: {
        'text-value': ['get', 'text'],
        'text-fill-color': ['get', 'color'],
        'text-stroke-width': 2,
        'text-stroke-color': ['get', 'backgroundColor'],
        'text-offset-x': nodeSize * 2 + 4,
        'text-offset-y': -(nodeSize * 2 + 4),
        'text-align': 'left',
        'text-rotate-with-view': true,
        'text-font': '14px Calibri,sans-serif',
      },
    },
    {
      filter: ['==', ['get', 'type'], 'interconnect'],
      style: {
        'text-value': ['get', 'text'],
        'text-fill-color': ['get', 'color'],
        'text-stroke-width': 2,
        'text-stroke-color': ['get', 'backgroundColor'],
        'text-offset-y': 0,
        'text-rotate-with-view': false,
        // 'text-rotation': ['get', 'angle'],
        'text-align': 'center',
        'text-font': 'bold 16px Calibri,sans-serif',
      },
    },
    {
      filter: ['==', ['get', 'type'], 'compressor'],
      style: {
        'text-value': ['get', 'text'],
        'text-fill-color': ['get', 'color'],
        'text-stroke-width': 3,
        'text-stroke-color': ['get', 'backgroundColor'],
        'text-offset-x': nodeSize * 2 + 6,
        'text-offset-y': -(nodeSize * 2 + 6),
        'text-align': 'left',
        'text-rotate-with-view': true,
        'text-font': 'bold 16px Calibri,sans-serif',
      },
    },
  ];
};

export type MapLabelsProps = {
  zIndex: number;
};

export const MapLabels: React.FC<MapLabelsProps> = ({ zIndex }) => {
  const [theme] = useTheme();
  const allNodes = useSelector(selectAllNodes);
  const allPoints = useSelector(selectAllPoints);
  const nodes = useSelector(selectFilteredNodes);
  const points = useSelector(selectFilteredPoints);
  const nodeSize = useSelector(selectNodeSize);
  const interconnects = useSelector(selectFilteredInterconnects);
  const filteredTsps = useSelector(selectFilteredTsps);
  const {
    compressors: compressorsDisplay,
    interconnects: interconnectsDisplay,
    points: pointsDisplay,
  } = useSelector(selectLabelDisplayOptions);
  const mapZoom = useMapZoom();

  const showCompressorLabels =
    nodeSize > 0 &&
    compressorsDisplay !== 'HIDE' &&
    (compressorsDisplay === 'SHOW' ||
      mapZoom > COMPRESSOR_LABEL_ZOOM_THRESHOLD);
  const showInterconnectLabels =
    interconnectsDisplay !== 'HIDE' &&
    (interconnectsDisplay === 'SHOW' ||
      mapZoom > INTERCONNECT_LABEL_ZOOM_THRESHOLD);
  const showPointLabels =
    pointsDisplay !== 'HIDE' &&
    (pointsDisplay === 'SHOW' || mapZoom > POINT_LABEL_ZOOM_THRESHOLD);

  const showLabels = filteredTsps.length <= MAX_TSPS_LABELS_THRESHOLD;

  const compressors = useMemo(() => {
    // return Object.values(points).filter((point) => {
    //   const node = allNodes[point.node ?? ''];
    //   if (!node) return false;
    //   return isCompressor(node, points);
    // });
    return Object.values(nodes)
      .filter((node) => {
        if (
          node === null ||
          node === undefined ||
          !node.points ||
          node.points.length === 0
        )
          return false;
        return isCompressor(node, points);
      })
      .map((node) => {
        const compressorId = node.points.find(
          (pointId) => allPoints[pointId]?.loc_type_ind === 'COM',
        );
        if (!compressorId) return null;
        return allPoints[compressorId];
      })
      .filter(Boolean);
  }, [points, nodes]);

  const nonCompressors = useMemo(() => {
    return Object.values(nodes)
      .filter((node) => {
        if (
          node === null ||
          node === undefined ||
          !node.points ||
          node.points.length === 0
        )
          return false;
        return !isCompressor(node, points);
      })
      .map((node) => {
        return node.points
          .map((pointId) => allPoints[pointId])
          .filter((point) => point && point.loc_type_ind !== 'COM');
      })
      .filter((points) => points.length > 0);
  }, [points, nodes]);

  const pointFeatures = useMemo<FeatureLike[]>(() => {
    const backgroundColors = {
      R: colors.elements.receiptNode,
      D: colors.elements.deliveryNode,
      B: colors.elements.bidirectionalNode,
    };
    return nonCompressors
      .map((points) => {
        const node = allNodes[points[0].node ?? ''];
        if (!node) return null;
        const color =
          theme === 'light'
            ? backgroundColors[
                points[0].dir_flo as keyof typeof backgroundColors
              ] ?? colors.dark
            : colors.themes.dark.labelColor;
        const backgroundColor =
          theme === 'light'
            ? colors.themes.light.labelBackground
            : backgroundColors[
                points[0].dir_flo as keyof typeof backgroundColors
              ] ?? colors.dark;
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: fromLonLat([
              Number(node.lng ?? 0),
              Number(node.lat ?? 0),
            ]),
          },
          properties: {
            id: node.id,
            key: `node-${node.id}`,
            text: points
              .map((point) => {
                const poolNames = point.price_components
                  .map((pc) => `\t\t\t\t\t• ${pc.description}`)
                  .join('\n');
                return poolNames.length > 0
                  ? `${point.name}\n\t\t\tPools:\n${poolNames}`
                  : point.name;
              })
              .join('\n'),
            type: 'point',
            color: color,
            backgroundColor: backgroundColor,
          },
        };
      })
      .filter((feature) => feature !== null) as FeatureLike[];
  }, [points, allNodes, theme]);

  const compressorFeatures = useMemo<FeatureLike[]>(() => {
    const color = colors.themes[theme].labelColor;
    const backgroundColor = colors.themes[theme].labelBackground;
    return compressors
      .map((point) => {
        if (!point) return null;
        const node = allNodes[point.node ?? ''];
        if (!node) return null;
        const poolsAtNode = node.points.flatMap((pointId) => {
          const p = allPoints[pointId];
          if (!p || p.price_components.length === 0) return [];
          return p.price_components;
        });
        const poolNames = poolsAtNode
          .map((pc) => `\t\t\t\t\t• ${pc.description}`)
          .join('\n');
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: fromLonLat([
              Number(node.lng ?? 0),
              Number(node.lat ?? 0),
            ]),
          },
          properties: {
            id: node.id,
            key: `node-${node.id}`,
            text:
              poolNames?.length > 0
                ? `${point.name}\n\t\t\tPools:\n${poolNames}`
                : point.name,
            type: 'compressor',
            color,
            backgroundColor,
          },
        };
      })
      .filter((feature) => feature !== null) as FeatureLike[];
  }, [compressors, allNodes, theme, showCompressorLabels]);

  const interconnectFeatures = useMemo<FeatureLike[]>(() => {
    const color = setLuminosity(
      setSaturation(colors.elements.interconnectColor, 70),
      30,
    );
    const backgroundColor = getContrastColor(color);
    return interconnects
      .map((interconnect) => {
        const startPoint = allPoints[String(interconnect.start_point)];
        const endPoint = allPoints[String(interconnect.end_point)];
        if (!startPoint || !endPoint) return null;

        const startNode = allNodes[startPoint.node ?? ''];
        const endNode = allNodes[endPoint.node ?? ''];
        if (!startNode || !endNode) return null;

        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: fromLonLat([
              (Number(startNode.lng ?? 0) + Number(endNode.lng ?? 0)) / 2,
              (Number(startNode.lat ?? 0) + Number(endNode.lat ?? 0)) / 2,
            ]),
          },
          properties: {
            id: interconnect.id,
            key: `interconnect-${interconnect.id}`,
            text:
              interconnect.alias ||
              `${startPoint.tsp_name} to ${endPoint.tsp_name}`,
            type: 'interconnect',
            color: theme === 'light' ? color : backgroundColor,
            backgroundColor: theme === 'light' ? backgroundColor : color,
          },
        };
      })
      .filter((feature) => feature !== null) as FeatureLike[];
  }, [interconnects, allPoints, allNodes, theme]);

  const allFeatures = useMemo(() => {
    if (!showLabels) return [];
    const features = {
      points: showPointLabels ? pointFeatures : [],
      compressors: showCompressorLabels ? compressorFeatures : [],
      interconnects: showInterconnectLabels ? interconnectFeatures : [],
    };
    return [
      ...features.points,
      ...features.interconnects,
      ...features.compressors,
    ];
  }, [
    compressorFeatures,
    interconnectFeatures,
    pointFeatures,
    showPointLabels,
    showCompressorLabels,
    showInterconnectLabels,
    showLabels,
  ]);

  const style = useMemo(() => getLabelStyle(nodeSize), [nodeSize]);

  useMapLayer(allFeatures, { zIndex, style, layerType: OlLayerType.Vector });

  return <></>;
};
